import { Users, Eye, Camera, Bell, HandHeart, Home, CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const PROMISES = [{
  Icon: Users,
  badge: "Continuity",
  title: "Same tech, every visit.",
  desc: "Your door has quirks — that buzz at 75%, that one cable bracket. Knowing the history matters. You won't get a different stranger every year."
}, {
  Icon: Eye,
  badge: "Proactive",
  title: "Catch wear before it breaks.",
  desc: "A spring with 6 months of life left looks identical to one with 6 years — to you. We measure cycle count, tension drop, and corrosion. You hear about it before it lets go."
}, {
  Icon: Camera,
  badge: "Documented",
  title: "Photo report you keep.",
  desc: "Stamped, dated, and photographed — pass/flag/fix-now on every checklist item. Insurance assessors love it. Strata managers demand it. You sleep better with it."
}, {
  Icon: Bell,
  badge: "Hands-off",
  title: "We remember so you don't.",
  desc: "On a maintenance plan? We'll text you a month before the next service is due, then call to lock in a 1-hour slot. Set once, forget forever."
}, {
  Icon: HandHeart,
  badge: "No upsell",
  title: "Honest \"watch this\" notes.",
  desc: "If a roller is wearing but has 12 months left, we say so — not 'replace it now'. Family business reputation matters more than a single oversold visit."
}, {
  Icon: Home,
  badge: "Local since 2009",
  title: "Sydney climate, Sydney knowledge.",
  desc: "Coastal salt mist eats cables. Western Sydney heat kills rubber seals. Storm season warps tracks. 17 years in this market means we know what your suburb does to a door."
}];
export default function ServiceWhyUs() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 left-10 pointer-events-none" style={{
      opacity: 0.5
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 right-10 pointer-events-none" style={{
      opacity: 0.5,
      transform: "rotate(180deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              06 · Six Promises We Keep
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            Why Sydney homes
            <br />
            <span style={{
            position: "relative",
            display: "inline-block",
            color: NAVY
          }}>
              stay on the plan
              <span aria-hidden style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "0.08em",
              height: "0.18em",
              backgroundColor: `${GOLD}55`,
              zIndex: -1
            }} />
            </span>
            .
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "640px"
        }}>
            Six commitments that turn a one-off visit into a long-term
            relationship. Each one tested over thousands of Sydney callouts —
            and yes, signed.
          </p>
        </div>

        {/* Promise grid 3x2 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROMISES.map(({
          Icon,
          badge,
          title,
          desc
        }, i) => <article key={title} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-2px] hover:shadow-xl" style={{
          backgroundColor: "#ffffff",
          border: `1px solid rgba(8,20,56,0.08)`,
          boxShadow: "0 6px 20px rgba(8,20,56,0.05)"
        }}>
              {/* Gold corner seal */}
              <div aria-hidden className="absolute pointer-events-none" style={{
            top: "0",
            right: "0",
            width: "70px",
            height: "70px",
            background: `linear-gradient(225deg, ${GOLD}22 0%, transparent 60%)`
          }} />

              {/* Seal checkmark */}
              <div className="absolute" style={{
            top: "16px",
            right: "16px",
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 10px ${GOLD}55`
          }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <CheckCircle2 size={14} color={NAVY} strokeWidth={3} />
                </span>
              </div>

              {/* Number stamp */}
              <div aria-hidden className="absolute pointer-events-none select-none" style={{
            bottom: "12px",
            right: "16px",
            fontFamily: FONT_HEAD,
            fontSize: "56px",
            fontWeight: 900,
            color: `${GOLD}12`,
            lineHeight: 1,
            letterSpacing: "-0.04em"
          }}>
                {String(i + 1).padStart(2, "0")}
              </div>

              <div className="p-7 md:p-8 relative">
                {/* Icon */}
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg mb-5" style={{
              width: 52,
              height: 52,
              backgroundColor: `${GOLD}15`,
              border: `1px solid ${GOLD}33`
            }}>
                  <Icon size={24} color={GOLD_DARK} strokeWidth={2.25} />
                </span>

                {/* Badge */}
                <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD_DARK,
              marginBottom: "8px"
            }}>
                  Promise {String(i + 1).padStart(2, "0")} · {badge}
                </div>

                {/* Title */}
                <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.25rem",
              lineHeight: 1.2,
              color: NAVY,
              letterSpacing: "-0.015em",
              marginBottom: "12px"
            }}>
                  {title}
                </h3>

                {/* Desc */}
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14px",
              lineHeight: 1.65,
              color: INK_SOFT
            }}>
                  {desc}
                </p>
              </div>
            </article>)}
        </div>

        {/* Bottom signature row */}
        <div className="mt-12 rounded-xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
          <div className="flex items-start gap-4">
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
            width: 44,
            height: 44,
            backgroundColor: `${GOLD}22`,
            border: `1px solid ${GOLD}44`
          }}>
              <HandHeart size={20} color={GOLD_DARK} strokeWidth={2.5} />
            </span>
            <div>
              <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.05rem",
              color: NAVY,
              marginBottom: "4px",
              letterSpacing: "-0.01em"
            }}>
                Signed by the Gold family. Honoured every visit.
              </h4>
              <p style={{
              fontFamily: FONT_BODY,
              fontSize: "13.5px",
              color: INK_SOFT,
              lineHeight: 1.55,
              maxWidth: "520px"
            }}>
                Bass Hill · Family-run since 2009 · Backed by 1-Year Labour
                Warranty on every job.
              </p>
            </div>
          </div>

          <a href="tel:0485000933" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "13px",
          boxShadow: `0 6px 18px ${NAVY}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={14} strokeWidth={2.5} />
            </span>
            Start a relationship
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={13} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>
    </section>;
}